/**
 * SoftPOS Mobile Terminal Page
 * ============================
 * 
 * Mobile-responsive tap-to-pay terminal using Stripe Payment Intents.
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Smartphone,
  CreditCard,
  ShoppingBag,
  Plus,
  Minus,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Loader2,
  Settings,
  Wifi,
  WifiOff,
  ArrowLeft,
  Receipt,
  User,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";

const POSSoftPOSPage = () => {
  const { token, user } = useAuth();
  
  // Config & Setup
  const [softposEnabled, setSoftposEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [devicePaired, setDevicePaired] = useState(false);
  
  // Products & Cart
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Customer
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  
  // Payment
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, "processing", "success", "failed"
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  
  useEffect(() => {
    checkSoftPOSConfig();
    fetchProducts();
  }, []);
  
  const checkSoftPOSConfig = async () => {
    try {
      const res = await fetch(`${API}/pos/softpos/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const config = await res.json();
        setSoftposEnabled(config.enabled);
        setDevicePaired(config.paired_devices?.length > 0);
      }
    } catch (err) {
      console.error("Error checking SoftPOS config:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API}/pos/products?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setProducts(await res.json());
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };
  
  const enableSoftPOS = async () => {
    try {
      const res = await fetch(`${API}/pos/softpos/config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: true, pin_required: true })
      });
      
      if (res.ok) {
        setSoftposEnabled(true);
        toast.success("SoftPOS enabled");
        
        // Auto-pair this device
        const pairRes = await fetch(`${API}/pos/softpos/pair-device`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (pairRes.ok) {
          setDevicePaired(true);
          toast.success("Device paired successfully");
        }
      }
    } catch (err) {
      toast.error("Failed to enable SoftPOS");
    }
  };
  
  // Cart operations
  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.product_id);
    if (existing) {
      setCart(cart.map(item =>
        item.product_id === product.product_id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.product_id,
        name: product.name,
        sku: product.sku,
        price: product.retail_price,
        quantity: 1
      }]);
    }
  };
  
  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  
  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };
  
  const clearCart = () => {
    setCart([]);
    setSelectedCustomer(null);
    setCurrentTransaction(null);
  };
  
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  
  // Customer search
  const searchCustomers = async (query) => {
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    try {
      const res = await fetch(`${API}/pos/customers/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCustomerResults(await res.json());
      }
    } catch (err) {
      console.error("Error searching customers:", err);
    }
  };
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (customerSearch) searchCustomers(customerSearch);
    }, 300);
    return () => clearTimeout(timeout);
  }, [customerSearch]);
  
  // Payment flow
  const initiatePayment = async () => {
    if (cart.length === 0) return;
    
    setPaymentStatus("processing");
    setPaymentError(null);
    setShowPaymentModal(true);
    
    try {
      // Create transaction first
      const txnRes = await fetch(`${API}/pos/transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: selectedCustomer?.customer_id || null
        })
      });
      
      if (!txnRes.ok) throw new Error("Failed to create transaction");
      const txn = await txnRes.json();
      setCurrentTransaction(txn);
      
      // Add items
      for (const item of cart) {
        await fetch(`${API}/pos/transactions/${txn.transaction_id}/items`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: item.price
          })
        });
      }
      
      // Simulate NFC payment (in production, this would use real NFC)
      const paymentRes = await fetch(`${API}/pos/softpos/simulate-nfc`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          transaction_id: txn.transaction_id,
          amount: cartTotal
        })
      });
      
      const result = await paymentRes.json();
      
      if (result.status === "succeeded") {
        setPaymentStatus("success");
        toast.success("Payment successful!");
        
        // Clear cart after delay
        setTimeout(() => {
          clearCart();
          setShowPaymentModal(false);
          setPaymentStatus(null);
        }, 3000);
      } else {
        setPaymentStatus("failed");
        setPaymentError(result.error || "Payment failed");
      }
      
    } catch (err) {
      setPaymentStatus("failed");
      setPaymentError(err.message);
    }
  };
  
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  
  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  // Setup screen if not enabled
  if (!softposEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900 flex flex-col items-center justify-center p-6">
        <Smartphone className="h-20 w-20 text-indigo-400 mb-6" />
        <h1 className="text-2xl font-bold mb-2">Enable SoftPOS</h1>
        <p className="text-gray-500 text-center mb-8 max-w-md">
          Turn your device into a payment terminal. Accept contactless payments with just a tap.
        </p>
        <Button
          onClick={enableSoftPOS}
          size="lg"
          className="bg-indigo-600 hover:bg-indigo-700 h-14 px-8"
          data-testid="enable-softpos-btn"
        >
          <Smartphone className="h-5 w-5 mr-2" />
          Enable SoftPOS
        </Button>
        <Link to="/merchant/pos" className="mt-4 text-gray-500 hover:text-gray-900">
          ← Back to POS Terminal
        </Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900 flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/merchant/pos" className="text-white/80 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-fuchsia-200" />
              <span className="font-semibold text-white">SoftPOS</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {devicePaired ? (
              <span className="flex items-center gap-1 text-emerald-400 text-sm">
                <Wifi className="h-4 w-4" />
                Connected
              </span>
            ) : (
              <span className="flex items-center gap-1 text-amber-400 text-sm">
                <WifiOff className="h-4 w-4" />
                Not paired
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(true)}
              className="text-gray-500"
            >
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content - Split View */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Products Section */}
        <div className="flex-1 flex flex-col overflow-hidden lg:border-r lg:border-gray-200">
          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="pl-10 bg-gray-100 border-gray-200"
              />
            </div>
          </div>
          
          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filteredProducts.map(product => (
                <button
                  key={product.product_id}
                  onClick={() => addToCart(product)}
                  className="bg-gray-100/50 rounded-xl p-3 text-left hover:bg-gray-100 transition-colors border border-white/5"
                  data-testid={`softpos-product-${product.product_id}`}
                >
                  <div className="aspect-square bg-slate-700/50 rounded-lg mb-2 flex items-center justify-center">
                    <ShoppingBag className="h-8 w-8 text-gray-500" />
                  </div>
                  <p className="font-medium text-sm truncate">{product.name}</p>
                  <p className="text-indigo-400 font-bold">{formatPrice(product.retail_price)}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Cart Section - Dark sidebar with proper contrast */}
        <div className="lg:w-96 flex flex-col bg-slate-800 border-t lg:border-t-0 lg:border-l border-slate-700">
          {/* Customer */}
          <div className="p-4 border-b border-slate-700">
            {selectedCustomer ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-indigo-400" />
                  <div>
                    <p className="font-medium text-sm text-white">{selectedCustomer.name}</p>
                    <p className="text-xs text-amber-400 flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      {selectedCustomer.loyalty_points} pts
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCustomer(null)}
                  className="text-slate-400 hover:text-white hover:bg-slate-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowCustomerSearch(!showCustomerSearch)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 border-indigo-500 text-white justify-start"
                >
                  <User className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
                
                {showCustomerSearch && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-slate-700 rounded-lg border border-slate-600 p-2 z-10">
                    <Input
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Search customer..."
                      className="bg-slate-600 border-slate-500 text-white text-sm placeholder:text-slate-400"
                      autoFocus
                    />
                    {customerResults.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                        {customerResults.map(c => (
                          <button
                            key={c.customer_id}
                            onClick={() => {
                              setSelectedCustomer(c);
                              setShowCustomerSearch(false);
                              setCustomerSearch("");
                            }}
                            className="w-full p-2 text-left rounded hover:bg-slate-700"
                          >
                            <p className="font-medium text-sm">{c.name}</p>
                            <p className="text-xs text-amber-400">{c.loyalty_points} pts</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center text-slate-400 py-8">
                <ShoppingBag className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Cart is empty</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map(item => (
                  <div key={item.product_id} className="flex items-center gap-3 bg-slate-700/50 rounded-lg p-3">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-white">{item.name}</p>
                      <p className="text-slate-400 text-xs">{formatPrice(item.price)} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateQuantity(item.product_id, -1)}
                        className="h-8 w-8 p-0 text-white hover:bg-slate-600"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center text-white">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateQuantity(item.product_id, 1)}
                        className="h-8 w-8 p-0 text-white hover:bg-slate-600"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromCart(item.product_id)}
                      className="text-slate-400 hover:text-red-400 hover:bg-slate-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Total & Pay */}
          <div className="p-4 border-t border-slate-700 space-y-3">
            <div className="flex justify-between items-center text-lg text-white">
              <span>Total</span>
              <span className="font-bold text-xl text-emerald-400">{formatPrice(cartTotal)}</span>
            </div>
            
            {cart.length > 0 && (
              <Button
                onClick={clearCart}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Clear Cart
              </Button>
            )}
            
            <Button
              onClick={initiatePayment}
              disabled={cart.length === 0}
              className="w-full h-16 bg-emerald-600 hover:bg-emerald-700 text-lg text-white"
              data-testid="softpos-pay-btn"
            >
              <CreditCard className="h-6 w-6 mr-3" />
              Tap to Pay
            </Button>
          </div>
        </div>
      </div>
      
      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-white border-gray-200 text-center max-w-sm">
          <DialogHeader>
            <DialogTitle className="sr-only">Payment</DialogTitle>
          </DialogHeader>
          
          <div className="py-8">
            {paymentStatus === "processing" && (
              <>
                <div className="relative w-32 h-32 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full border-4 border-indigo-500/30 animate-ping"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-indigo-500 flex items-center justify-center">
                    <CreditCard className="h-12 w-12 text-indigo-400" />
                  </div>
                </div>
                <h2 className="text-xl font-bold mb-2">Ready for Payment</h2>
                <p className="text-gray-500 mb-4">Tap card or phone on device</p>
                <p className="text-3xl font-bold text-indigo-400">{formatPrice(cartTotal)}</p>
              </>
            )}
            
            {paymentStatus === "success" && (
              <>
                <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="h-16 w-16 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold mb-2 text-emerald-400">Payment Successful</h2>
                <p className="text-gray-500 mb-4">Transaction complete</p>
                <p className="text-3xl font-bold">{formatPrice(cartTotal)}</p>
              </>
            )}
            
            {paymentStatus === "failed" && (
              <>
                <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                  <XCircle className="h-16 w-16 text-red-400" />
                </div>
                <h2 className="text-xl font-bold mb-2 text-red-400">Payment Failed</h2>
                <p className="text-gray-500 mb-4">{paymentError || "Please try again"}</p>
                <Button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentStatus(null);
                  }}
                  className="bg-slate-700 hover:bg-slate-600"
                >
                  Try Again
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle>SoftPOS Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">SoftPOS Enabled</p>
                <p className="text-sm text-gray-500">Accept tap-to-pay on this device</p>
              </div>
              <Switch checked={softposEnabled} disabled />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Device Status</p>
                <p className="text-sm text-gray-500">
                  {devicePaired ? "Paired and ready" : "Not paired"}
                </p>
              </div>
              {devicePaired ? (
                <CheckCircle className="h-5 w-5 text-emerald-400" />
              ) : (
                <Button
                  size="sm"
                  onClick={async () => {
                    const res = await fetch(`${API}/pos/softpos/pair-device`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.ok) {
                      setDevicePaired(true);
                      toast.success("Device paired");
                    }
                  }}
                >
                  Pair Device
                </Button>
              )}
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Test Cards:<br />
                Success: 4242 4242 4242 4242<br />
                Decline: 4000 0000 0000 0002
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSSoftPOSPage;
